// 092 -- Admin Provider edit/create form.
// Fields conditionally render by fulfillment_type.

import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import type { ProviderFulfillmentType, ProviderPublic } from "@dugads/shared";
import * as api from "@/lib/api";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Breadcrumbs } from "@/components/breadcrumbs";

interface FormState {
  name: string;
  slug: string;
  fulfillment_type: ProviderFulfillmentType;
  logo_url: string;
  description: string;
  discount_blurb: string;
  support_email: string;
  fulfillment_email: string;
  fulfilled_email_copy: string;
  external_url_template: string;
  external_cta_label: string;
  // 093 -- quote_form_config is stored as JSON; the admin UI edits it as a JSON
  // string (textarea) and only sends the parsed object on submit. Keeps the
  // per-field builder out of scope for this iteration.
  quote_form_config_json: string;
  quote_submit_handler: string;
  // 095 -- order_api fields
  api_endpoint_url: string;
  api_auth_scheme: "" | "bearer" | "hmac_sha256" | "basic";
  fulfilment_fallback_email: string;
  active: boolean;
  sort_order: number;
}

const EMPTY: FormState = {
  name: "",
  slug: "",
  fulfillment_type: "order_email",
  logo_url: "",
  description: "",
  discount_blurb: "",
  support_email: "",
  fulfillment_email: "",
  fulfilled_email_copy: "",
  external_url_template: "",
  external_cta_label: "",
  quote_form_config_json: "",
  quote_submit_handler: "",
  api_endpoint_url: "",
  api_auth_scheme: "",
  fulfilment_fallback_email: "",
  active: true,
  sort_order: 0,
};

export default function AdminShopProviderEditPage() {
  const { id } = useParams<{ id?: string }>();
  const isNew = !id;
  const navigate = useNavigate();

  const [form, setForm] = useState<FormState>(EMPTY);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [provider, setProvider] = useState<ProviderPublic | null>(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (isNew) return;
    api.getAdminProviders()
      .then((r) => {
        const p = r.providers.find((x) => x.id === id || x.slug === id);
        if (!p) throw new Error("Provider not found");
        setProvider(p);
        setForm({
          name: p.name,
          slug: p.slug,
          fulfillment_type: p.fulfillment_type,
          logo_url: p.logo_url ?? "",
          description: p.description ?? "",
          discount_blurb: p.discount_blurb ?? "",
          support_email: p.support_email ?? "",
          fulfillment_email: p.fulfillment_email ?? "",
          fulfilled_email_copy: p.fulfilled_email_copy ?? "",
          external_url_template: p.external_url_template ?? "",
          external_cta_label: p.external_cta_label ?? "",
          quote_form_config_json: p.quote_form_config ? JSON.stringify(p.quote_form_config, null, 2) : "",
          quote_submit_handler: p.quote_submit_handler ?? "",
          api_endpoint_url: p.api_endpoint_url ?? "",
          api_auth_scheme: p.api_auth_scheme ?? "",
          fulfilment_fallback_email: p.fulfilment_fallback_email ?? "",
          active: p.active,
          sort_order: p.sort_order,
        });
      })
      .catch((e) => setError(e instanceof Error ? e.message : "Failed to load"))
      .finally(() => setLoading(false));
  }, [id, isNew]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      // 093 -- parse quote_form_config JSON (leave null for non-quote types or when blank)
      let quoteFormConfig: unknown = undefined;
      if (form.quote_form_config_json.trim() !== "") {
        try {
          quoteFormConfig = JSON.parse(form.quote_form_config_json);
        } catch {
          throw new Error("quote_form_config is not valid JSON");
        }
      } else if (form.fulfillment_type !== "quote") {
        quoteFormConfig = null;
      }
      const payload = {
        name: form.name,
        slug: form.slug || undefined,
        fulfillment_type: form.fulfillment_type,
        logo_url: form.logo_url,
        description: form.description,
        discount_blurb: form.discount_blurb,
        support_email: form.support_email,
        fulfillment_email: form.fulfillment_email,
        fulfilled_email_copy: form.fulfilled_email_copy,
        external_url_template: form.external_url_template,
        external_cta_label: form.external_cta_label,
        api_endpoint_url: form.api_endpoint_url,
        api_auth_scheme: form.api_auth_scheme || null,
        fulfilment_fallback_email: form.fulfilment_fallback_email,
        quote_form_config: quoteFormConfig,
        quote_submit_handler: form.quote_submit_handler,
        active: form.active,
        sort_order: form.sort_order,
      };
      if (isNew) {
        const r = await api.createAdminProvider(payload);
        navigate(`/admin/shop/providers/${r.provider.id}/edit`);
      } else {
        const r = await api.updateAdminProvider(id!, payload);
        setProvider(r.provider);
      }
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  async function handleDeactivate() {
    if (!provider) return;
    if (!confirm(`Deactivate "${provider.name}"? It will disappear from the shop.`)) return;
    try {
      await api.deleteAdminProvider(provider.id);
      navigate("/admin/shop/providers");
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to deactivate");
    }
  }

  async function handleLogoUpload(e: React.ChangeEvent<HTMLInputElement>) {
    if (!provider) return;
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const r = await api.uploadAdminProviderLogo(provider.id, file);
      setProvider(r.provider);
      setForm((f) => ({ ...f, logo_url: r.provider.logo_url ?? "" }));
    } catch (err) {
      setError(err instanceof api.ApiError ? err.message : "Logo upload failed");
    } finally {
      setUploading(false);
    }
  }

  if (loading) return <p className="text-muted-foreground" data-page="admin-shop-provider-edit" data-state="loading">Loading…</p>;

  return (
    <div data-page="admin-shop-provider-edit" data-state={saving ? "saving" : "ready"} className="flex flex-col gap-6">
      <Breadcrumbs
        items={[
          { label: "Admin" },
          { label: "Shop providers", to: "/admin/shop/providers" },
          { label: isNew ? "New" : form.name || "Edit" },
        ]}
      />
      <div className="flex flex-wrap items-start justify-between gap-4">
        <h1 className="text-2xl font-semibold">{isNew ? "New provider" : `Edit ${provider?.name ?? "provider"}`}</h1>
        {!isNew && provider?.active && (
          <Button variant="outline" size="sm" onClick={handleDeactivate} data-action="deactivate">Deactivate</Button>
        )}
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-6" data-form="provider">
        <Card>
          <CardHeader><CardTitle className="text-base">Basics</CardTitle></CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-2">
              <Label htmlFor="name">Name</Label>
              <Input id="name" data-field="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="slug">Slug <span className="text-xs text-muted-foreground">(auto-generated if blank)</span></Label>
              <Input id="slug" data-field="slug" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="fulfillment_type">Fulfilment type</Label>
              <select
                id="fulfillment_type"
                data-field="fulfillment_type"
                value={form.fulfillment_type}
                onChange={(e) => setForm({ ...form, fulfillment_type: e.target.value as ProviderFulfillmentType })}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                aria-label="Fulfilment type"
              >
                <option value="order_email">Order email (local catalog, we forward the order)</option>
                <option value="external_redirect">External redirect (link-out to provider site)</option>
                <option value="quote">Quote (declarative form → provider API)</option>
                <option value="order_api">Order API (dispatch to provider HTTP endpoint)</option>
                <option value="cashback">Cashback (parent links account, we ingest spend batches)</option>
              </select>
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="sort_order">Sort order</Label>
              <Input
                id="sort_order"
                data-field="sort_order"
                type="number"
                value={form.sort_order}
                onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) || 0 })}
              />
            </div>
            <div className="flex flex-col gap-2 sm:col-span-2">
              <Label htmlFor="discount_blurb">Discount blurb <span className="text-xs text-muted-foreground">(shown on tile)</span></Label>
              <Input id="discount_blurb" data-field="discount_blurb" value={form.discount_blurb} onChange={(e) => setForm({ ...form, discount_blurb: e.target.value })} maxLength={200} />
            </div>
            <div className="flex flex-col gap-2 sm:col-span-2">
              <Label htmlFor="description">Description <span className="text-xs text-muted-foreground">(markdown supported)</span></Label>
              <textarea
                id="description"
                data-field="description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={4}
                className="min-h-24 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                aria-label="Description"
                placeholder="What does this provider offer?"
              />
            </div>
            <div className="flex items-center gap-2 sm:col-span-2">
              <input
                id="active"
                data-field="active"
                type="checkbox"
                aria-label="Active"
                checked={form.active}
                onChange={(e) => setForm({ ...form, active: e.target.checked })}
              />
              <Label htmlFor="active">Active (visible in shop)</Label>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Contact</CardTitle></CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-2">
              <Label htmlFor="support_email">Support email <span className="text-xs text-muted-foreground">(for customer queries)</span></Label>
              <Input id="support_email" data-field="support_email" type="email" value={form.support_email} onChange={(e) => setForm({ ...form, support_email: e.target.value })} />
            </div>
            {form.fulfillment_type === "order_email" && (
              <div className="flex flex-col gap-2">
                <Label htmlFor="fulfillment_email">Fulfilment email <span className="text-xs text-destructive">*</span></Label>
                <Input
                  id="fulfillment_email"
                  data-field="fulfillment_email"
                  type="email"
                  value={form.fulfillment_email}
                  onChange={(e) => setForm({ ...form, fulfillment_email: e.target.value })}
                  placeholder="orders@provider.com"
                  required
                />
                <p className="text-xs text-muted-foreground">Order emails go here when customers submit orders.</p>
              </div>
            )}
            {(form.fulfillment_type === "order_email" || form.fulfillment_type === "order_api") && (
              <div className="flex flex-col gap-2 sm:col-span-2">
                <Label htmlFor="fulfilled_email_copy">Custom &ldquo;fulfilled&rdquo; email copy <span className="text-xs text-muted-foreground">(optional)</span></Label>
                <textarea
                  id="fulfilled_email_copy"
                  data-field="fulfilled_email_copy"
                  value={form.fulfilled_email_copy}
                  onChange={(e) => setForm({ ...form, fulfilled_email_copy: e.target.value })}
                  rows={3}
                  className="min-h-20 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  aria-label="Custom fulfilled email copy"
                  placeholder="e.g. Your order is ready for collection at our King William's Town store."
                  maxLength={2000}
                />
                <p className="text-xs text-muted-foreground">Replaces the neutral default in the email sent to buyers when an order is marked Fulfilled. Plain text, 1&ndash;3 sentences. Leave blank for the default.</p>
              </div>
            )}
          </CardContent>
        </Card>

        {form.fulfillment_type === "quote" && (
          <Card>
            <CardHeader><CardTitle className="text-base">Quote form</CardTitle></CardHeader>
            <CardContent className="flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                <Label htmlFor="quote_submit_handler">Submit handler <span className="text-xs text-destructive">*</span></Label>
                <Input
                  id="quote_submit_handler"
                  data-field="quote_submit_handler"
                  value={form.quote_submit_handler}
                  onChange={(e) => setForm({ ...form, quote_submit_handler: e.target.value })}
                  placeholder="king_price_v1"
                  required
                />
                <p className="text-xs text-muted-foreground">
                  Registry key pointing to the server-side handler. Registered handlers: <code className="rounded bg-muted px-1">king_price_v1</code>.
                </p>
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="quote_form_config_json">Form config (JSON) <span className="text-xs text-destructive">*</span></Label>
                <textarea
                  id="quote_form_config_json"
                  data-field="quote_form_config_json"
                  value={form.quote_form_config_json}
                  onChange={(e) => setForm({ ...form, quote_form_config_json: e.target.value })}
                  rows={12}
                  className="min-h-32 w-full rounded-md border border-input bg-background px-3 py-2 text-xs font-mono"
                  aria-label="Quote form config JSON"
                  placeholder={`{\n  "fields": [\n    { "name": "first_name", "label": "First name", "type": "text", "required": true, "default_from_profile": "first_name" }\n  ]\n}`}
                  required
                />
                <p className="text-xs text-muted-foreground">
                  Declarative form schema. Field types: <code>text</code>, <code>email</code>, <code>phone</code>, <code>select</code>, <code>textarea</code>, <code>date</code>. <code>default_from_profile</code> auto-fills from the user's profile (first_name, last_name, phone_number, email, display_name).
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {form.fulfillment_type === "external_redirect" && (
          <Card>
            <CardHeader><CardTitle className="text-base">External redirect</CardTitle></CardHeader>
            <CardContent className="flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                <Label htmlFor="external_url_template">URL template <span className="text-xs text-destructive">*</span></Label>
                <Input
                  id="external_url_template"
                  data-field="external_url_template"
                  type="url"
                  value={form.external_url_template}
                  onChange={(e) => setForm({ ...form, external_url_template: e.target.value })}
                  placeholder="https://provider.com/?ref={school_slug}"
                  required
                />
                <p className="text-xs text-muted-foreground">
                  Available placeholders: <code className="rounded bg-muted px-1">{"{school_slug}"}</code>,{" "}
                  <code className="rounded bg-muted px-1">{"{user_id}"}</code>. URL-encoded at resolve time.
                </p>
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="external_cta_label">CTA button label</Label>
                <Input
                  id="external_cta_label"
                  data-field="external_cta_label"
                  value={form.external_cta_label}
                  onChange={(e) => setForm({ ...form, external_cta_label: e.target.value })}
                  placeholder="Shop Provider (8% off)"
                />
              </div>
            </CardContent>
          </Card>
        )}

        {form.fulfillment_type === "order_api" && (
          <Card>
            <CardHeader><CardTitle className="text-base">API dispatch</CardTitle></CardHeader>
            <CardContent className="flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                <Label htmlFor="api_endpoint_url">Endpoint URL <span className="text-xs text-destructive">*</span></Label>
                <Input
                  id="api_endpoint_url"
                  data-field="api_endpoint_url"
                  type="url"
                  value={form.api_endpoint_url}
                  onChange={(e) => setForm({ ...form, api_endpoint_url: e.target.value })}
                  placeholder="https://api.provider.com/orders"
                  required
                />
                <p className="text-xs text-muted-foreground">
                  Dugads POSTs a JSON envelope here on every order submission. See Shop Providers admin docs for the payload shape.
                </p>
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="api_auth_scheme">Auth scheme <span className="text-xs text-destructive">*</span></Label>
                <select
                  id="api_auth_scheme"
                  data-field="api_auth_scheme"
                  value={form.api_auth_scheme}
                  onChange={(e) => setForm({ ...form, api_auth_scheme: e.target.value as FormState["api_auth_scheme"] })}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  aria-label="Auth scheme"
                  required
                >
                  <option value="">Select…</option>
                  <option value="bearer">Bearer (secret: api_token)</option>
                  <option value="basic">Basic (secrets: api_user + api_password)</option>
                  <option value="hmac_sha256">HMAC-SHA256 body signature (secret: hmac_shared_secret)</option>
                </select>
                <p className="text-xs text-muted-foreground">Credentials live in the Secrets table below.</p>
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="fulfilment_fallback_email">Fallback email</Label>
                <Input
                  id="fulfilment_fallback_email"
                  data-field="fulfilment_fallback_email"
                  type="email"
                  value={form.fulfilment_fallback_email}
                  onChange={(e) => setForm({ ...form, fulfilment_fallback_email: e.target.value })}
                  placeholder="orders-fallback@provider.com"
                />
                <p className="text-xs text-muted-foreground">
                  When all API retries fail, Dugads sends the 090 provider-email template here. Falls back to <code>fulfillment_email</code> if this is blank.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {form.fulfillment_type === "order_api" && !isNew && provider && (
          <ProviderSecretsCard providerId={provider.id} authScheme={form.api_auth_scheme || null} />
        )}

        {form.fulfillment_type === "cashback" && !isNew && provider && (
          <Card>
            <CardHeader><CardTitle className="text-base">Cashback</CardTitle></CardHeader>
            <CardContent className="flex flex-col gap-3">
              <p className="text-sm text-muted-foreground">
                Manage the three-way split, ingest spend batches, and view the ledger on the dedicated cashback page.
              </p>
              <Link
                to={`/admin/shop/providers/${provider.id}/cashback`}
                className={buttonVariants({ variant: "outline", size: "sm" })}
                data-action="open-cashback"
              >
                Open cashback dashboard
              </Link>
            </CardContent>
          </Card>
        )}

        {!isNew && provider && (
          <Card>
            <CardHeader><CardTitle className="text-base">Logo</CardTitle></CardHeader>
            <CardContent className="flex items-center gap-4">
              {form.logo_url ? (
                <img src={form.logo_url} alt="Logo preview" className="h-16 w-16 rounded border bg-white object-contain p-1" />
              ) : (
                <div className="flex h-16 w-16 items-center justify-center rounded border bg-muted text-xs text-muted-foreground">
                  No logo
                </div>
              )}
              <label className={buttonVariants({ variant: "outline", size: "sm" })}>
                {uploading ? "Uploading…" : "Upload logo"}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/svg+xml"
                  onChange={handleLogoUpload}
                  className="hidden"
                  data-field="logo-file"
                  aria-label="Upload logo"
                />
              </label>
              <p className="text-xs text-muted-foreground">JPEG / PNG / WebP / SVG, 5MB max.</p>
            </CardContent>
          </Card>
        )}

        {error && <p className="text-sm text-destructive" data-error>{error}</p>}

        <div className="flex gap-2">
          <Button type="submit" disabled={saving} data-action="save">
            {saving ? "Saving…" : isNew ? "Create provider" : "Save changes"}
          </Button>
          <Button type="button" variant="outline" onClick={() => navigate("/admin/shop/providers")} data-action="cancel">
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}

// 095 -- Secrets management card. Values are write-only by default; admins
// can reveal one-at-a-time (audit-logged). "Rotate" lands a new ciphertext.
function ProviderSecretsCard({ providerId, authScheme }: { providerId: string; authScheme: string | null }) {
  const [secrets, setSecrets] = useState<Array<{ name: string; rotated_at: string; value?: string }>>([]);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [form, setForm] = useState<{ name: string; value: string }>({ name: "", value: "" });
  const [saving, setSaving] = useState(false);
  const [reveal, setReveal] = useState<string | null>(null);

  const expectedNames = (() => {
    switch (authScheme) {
      case "bearer": return ["api_token"];
      case "basic": return ["api_user", "api_password"];
      case "hmac_sha256": return ["hmac_shared_secret"];
      default: return [];
    }
  })();

  async function load(revealName?: string) {
    try {
      const r = await api.getProviderSecrets(providerId, revealName);
      setSecrets(r.secrets);
      setLoadErr(null);
    } catch (e) {
      setLoadErr(e instanceof api.ApiError ? e.message : "Failed to load secrets");
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerId]);

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      await api.upsertProviderSecret(providerId, form.name, form.value);
      setForm({ name: "", value: "" });
      await load();
    } catch (e) {
      setLoadErr(e instanceof api.ApiError ? e.message : "Failed to save secret");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(name: string) {
    if (!confirm(`Delete secret "${name}"?`)) return;
    try {
      await api.upsertProviderSecret(providerId, name, "");
      await load();
    } catch (e) {
      setLoadErr(e instanceof api.ApiError ? e.message : "Failed to delete secret");
    }
  }

  async function handleReveal(name: string) {
    setReveal(name);
    await load(name);
  }

  return (
    <Card data-region="secrets">
      <CardHeader><CardTitle className="text-base">Secrets</CardTitle></CardHeader>
      <CardContent className="flex flex-col gap-4">
        {loadErr && <p className="text-sm text-destructive" data-error>{loadErr}</p>}
        {expectedNames.length > 0 && (
          <p className="text-xs text-muted-foreground">
            Expected for <code>{authScheme}</code>: {expectedNames.map((n) => <code key={n} className="mr-1 rounded bg-muted px-1">{n}</code>)}
          </p>
        )}
        {secrets.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-state="empty">No secrets stored.</p>
        ) : (
          <table className="w-full text-sm" data-region="secrets-list">
            <thead>
              <tr className="border-b border-border text-xs uppercase text-muted-foreground">
                <th className="py-2 text-left">Name</th>
                <th className="py-2 text-left">Value</th>
                <th className="py-2 text-left">Rotated</th>
                <th className="py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {secrets.map((s) => (
                <tr key={s.name} className="border-b border-border/60" data-item="secret" data-item-name={s.name}>
                  <td className="py-2 font-mono text-xs">{s.name}</td>
                  <td className="py-2 font-mono text-xs">
                    {s.value ? (
                      <span data-field="reveal-value">{s.value}</span>
                    ) : (
                      <Button type="button" variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => handleReveal(s.name)} data-action="reveal">Reveal</Button>
                    )}
                  </td>
                  <td className="py-2 text-xs text-muted-foreground">{new Date(s.rotated_at).toLocaleString()}</td>
                  <td className="py-2 text-right">
                    <Button type="button" variant="ghost" size="sm" className="h-6 px-2 text-xs text-destructive" onClick={() => handleDelete(s.name)} data-action="delete">Delete</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <form onSubmit={handleSave} className="grid gap-3 sm:grid-cols-[1fr_2fr_auto]" data-form="secret">
          <Input
            placeholder="Secret name (e.g. api_token)"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            data-field="name"
            required
          />
          <Input
            placeholder="Value (encrypted on save)"
            value={form.value}
            onChange={(e) => setForm({ ...form, value: e.target.value })}
            data-field="value"
            required
          />
          <Button type="submit" size="sm" disabled={saving} data-action="upsert-secret">
            {saving ? "Saving…" : "Save"}
          </Button>
        </form>
        <p className="text-xs text-muted-foreground">
          Secrets are encrypted at rest with AES-256-GCM. Rotations create a fresh ciphertext -- the old one is overwritten. Revealing a secret writes an INFO log entry with the actor's user id.
        </p>
      </CardContent>
    </Card>
  );
}
